import type { KpiKey, KpiRules, PerformanceField, Status, TargetField } from '../api/types';

/**
 * Mirrors the backend/report-service evaluation of config/kpi-rules.json so
 * the grid can recolour derived metrics while the user is still typing.
 * The rules themselves always come from the API.
 */
export function evaluateKpi(rules: KpiRules, key: KpiKey, value: number | null): Status | null {
  if (value === null || !Number.isFinite(value)) return null;
  const band = rules.kpis[key].bands.find(
    (b) =>
      (b.gt === undefined || value > b.gt) &&
      (b.gte === undefined || value >= b.gte) &&
      (b.lt === undefined || value < b.lt) &&
      (b.lte === undefined || value <= b.lte),
  );
  return band?.status ?? null;
}

export function salesStatus(sales: number | null, minimum: number | null, target: number | null): Status | null {
  if (sales === null) return null;
  if (target !== null && sales > target) return 'green';
  if (minimum !== null && sales < minimum) return 'red';
  if (minimum !== null) return 'yellow';
  return null;
}

const ratio = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);

export type Values = Record<PerformanceField | TargetField, number | null>;

export const DERIVED_COLUMNS: { key: KpiKey; label: string; short: string; format: 'percent' | 'number' }[] = [
  { key: 'leadsPerDay', label: 'Leads por día trabajado', short: 'Leads/día', format: 'number' },
  { key: 'leadToOpportunityRate', label: 'Conversión Lead → Oportunidad', short: 'L→O', format: 'percent' },
  { key: 'opportunityToSaleRate', label: 'Conversión Oportunidad → Venta', short: 'O→V', format: 'percent' },
  { key: 'leadToSaleRate', label: 'Tasa de conversión Lead → Venta', short: 'L→V', format: 'percent' },
  { key: 'callHoursPerDay', label: 'Horas de llamada por día', short: 'h llamada/día', format: 'number' },
  { key: 'visitsPerDay', label: 'Visitas presenciales por día', short: 'Visitas/día', format: 'number' },
];

export function deriveMetrics(v: Values): Record<KpiKey, number | null> {
  return {
    leadsPerDay: ratio(v.leads, v.daysWorked),
    leadToOpportunityRate: ratio(v.opportunities, v.leads),
    opportunityToSaleRate: ratio(v.sales, v.opportunities),
    leadToSaleRate: ratio(v.sales, v.leads),
    callHoursPerDay: ratio(v.callHours, v.daysWorked),
    visitsPerDay: ratio(v.visits, v.daysWorked),
  };
}
